import Service from './Service';
import { AppNotification, MarkReadResult, NotificationList, NotificationType } from '../@types/Notificacao';

export interface ListNotificationsParams {
  /**
   * Só as não lidas. **Só vai no fio quando verdadeiro** — o Go trata
   * `?unreadOnly` vazio como "sim" (`parseBoolParam`), então mandar a chave com
   * `false` é ambíguo por construção; mandar `unreadOnly=false` funcionaria, mas
   * a ausência é o que o contrato descreve como "sem filtro".
   */
  unreadOnly?: boolean;
}

/**
 * Notificações in-system do próprio usuário — as duas rotas que **já existem**
 * no Go (`back/cmd/api/notifications.go`, `authGroup`, sem guarda de perfil e
 * sem gate de assinatura).
 *
 * Toda divergência de contrato é absorvida **aqui**, nunca na tela (decisão 9 do
 * design), no mesmo espírito de `QuestionReportService.normalizeGroup` e
 * `PdfHighlightService.normalize`: o serviço aceita o corpo canônico
 * `{notifications, unread, total}` **ou** um array cru, e completa
 * `readAt`/`questionId` ausentes com `null`. Se o Go mudar, muda este arquivo.
 */
class NotificationService extends Service {
  /* ---------------------------------------------------------- normalização */

  /**
   * Número vindo da API, com piso de sanidade. `undefined`, `null`, string
   * vazia e qualquer coisa que não vire número finito caem no fallback.
   *
   * A string vazia precisa da checagem explícita porque `Number('')` é `0`, e
   * um zero *inventado* é pior que o derivado: esconderia o badge em vez de
   * mostrar a contagem que a lista já permite calcular. `NaN`, pelo mesmo
   * motivo ao contrário, pintaria "NaN" na tela.
   */
  private static toCount(raw: unknown, fallback: number): number {
    if (raw === undefined || raw === null || raw === '') return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  }

  /** `null` explícito para ausente/vazio; qualquer outra coisa vira string. */
  private static toNullableString(raw: unknown): string | null {
    return raw === undefined || raw === null || raw === '' ? null : String(raw);
  }

  /**
   * O tipo é **preservado como veio**, mesmo fora da união conhecida.
   *
   * Pelo Migration Plan o back ganha tipos novos antes do front, e nenhuma tela
   * ramifica neste campo — descartar a notificação ou trocar o tipo por um
   * fallback esconderia do usuário um aviso que existe e que ele precisa ler.
   */
  private static toType(raw: unknown): NotificationType {
    return String(raw ?? '') as NotificationType;
  }

  private static normalizeItem(raw: any): AppNotification {
    return {
      id: String(raw?.id ?? raw?._id ?? ''),
      userId: String(raw?.userId ?? raw?.user_id ?? ''),
      type: this.toType(raw?.type),
      title: String(raw?.title ?? ''),
      body: String(raw?.body ?? ''),
      questionId: this.toNullableString(raw?.questionId ?? raw?.question_id),
      readAt: this.toNullableString(raw?.readAt ?? raw?.read_at),
      createdAt: String(raw?.createdAt ?? raw?.created_at ?? ''),
    };
  }

  /**
   * Aceita `{notifications, unread, total}` (o contrato) ou um array cru (a
   * divergência plausível: um handler que devolva a lista sem envelope).
   *
   * No array cru os dois contadores são **derivados**, não inventados: `total` é
   * o tamanho da lista e `unread` são os itens com `readAt` nulo — coerentes
   * entre si e com a lista devolvida. Com envelope, `unread` vem da API, que é a
   * única fonte que conhece as não lidas fora desta página.
   *
   * `total` só é aceito do envelope quando o envelope **de fato trouxe uma
   * lista**: no Go ele é literalmente `len(items)`, então um `total: 3` ao lado
   * de um `notifications` ilegível descreveria uma lista que não chegou. Nesse
   * caso ele passa a ser o tamanho do que o chamador realmente recebeu —
   * absorver a divergência não pode virar afirmar uma contagem falsa. `unread`
   * não segue essa regra de propósito: ele conta as não lidas do usuário
   * inteiro, fora desta página, e continua válido mesmo sem lista.
   */
  private static normalizeList(raw: any): NotificationList {
    const envelope = Array.isArray(raw) ? undefined : raw;
    const listaDoEnvelope = Array.isArray(envelope?.notifications);
    const items: any[] = Array.isArray(raw)
      ? raw
      : listaDoEnvelope
        ? envelope.notifications
        : [];
    const notifications = items.map((item) => this.normalizeItem(item));

    return {
      notifications,
      unread: this.toCount(
        envelope?.unread,
        notifications.filter((n) => n.readAt === null).length
      ),
      total: listaDoEnvelope
        ? this.toCount(envelope.total, notifications.length)
        : notifications.length,
    };
  }

  /* --------------------------------------------------------------- leitura */

  /**
   * `GET /notifications` — mais recentes primeiro, com a contagem de não lidas.
   *
   * Sem `unreadOnly` verdadeiro a chamada sai **sem params**: é a rota nua, e
   * nenhum `?unreadOnly=false` aparece no fio.
   */
  static list(params?: ListNotificationsParams) {
    const request = params?.unreadOnly
      ? this.Http.get<unknown>('/notifications', { params: { unreadOnly: true } })
      : this.Http.get<unknown>('/notifications');

    return request.then(this.getData).then((raw) => this.normalizeList(raw));
  }

  /**
   * `POST /notifications/:id/read` — idempotente no back (remarcar uma já lida
   * devolve 200 sem alterar `readAt`); notificação de outro usuário devolve
   * `404`, sem revelar que existe.
   *
   * O `unread` da resposta é a nova contagem do badge. Se o back divergir e não
   * mandá-lo, o fallback é `0` — o badge esconde o número em vez de exibir um
   * decremento local que pode discordar da verdade do servidor.
   */
  static markRead(id: string): Promise<MarkReadResult> {
    return this.Http.post<unknown>(`/notifications/${id}/read`)
      .then(this.getData)
      .then((raw: any) => ({
        notification: this.normalizeItem(raw?.notification ?? raw),
        unread: this.toCount(raw?.unread, 0),
      }));
  }
}

export default NotificationService;
